// client/src/components/react-dashboard/org_form/OrganizationSetup.jsx

import React, { useMemo, useState } from 'react';

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import { Formik } from 'formik';
import * as yup from 'yup';

import {
  useCompleteMyOrganizationSetupMutation,
} from '../../../features/organizations/organizationsApiSlice';

import {
  useRefreshMutation,
} from '../../../features/auth/authApiSlice';


const organizationSchema =
  yup.object().shape({
    name:
      yup
        .string()
        .trim()
        .required('Organization name is required'),

    email:
      yup
        .string()
        .trim()
        .email('Enter a valid email address')
        .required('Email is required'),

    phone:
      yup
        .string()
        .trim(),

    address:
      yup
        .string()
        .trim(),

    type:
      yup
        .string()
        .trim()
        .required('Organization type is required'),
  });


const OrganizationSetup = ({
  open,
  organization,
  onClose,
  onCompleted,
}) => {
  const theme = useTheme();

  const fullScreen =
    useMediaQuery(
      theme.breakpoints.down('sm')
    );

  const [
    submitError,
    setSubmitError,
  ] = useState('');

  const [
    completeSetup,
    {
      isLoading:
        isCompletingSetup,
    },
  ] =
    useCompleteMyOrganizationSetupMutation();

  const [
    refresh,
    {
      isLoading:
        isRefreshing,
    },
  ] =
    useRefreshMutation();


  const initialValues =
    useMemo(
      () => ({
        name:
          organization?.name || '',

        email:
          organization?.email || '',

        phone:
          organization?.phone || '',

        address:
          organization?.address || '',

        type:
          organization?.type || '',
      }),
      [organization]
    );


  const handleClose = () => {
    if (
      isCompletingSetup ||
      isRefreshing
    ) {
      return;
    }

    setSubmitError('');

    if (onClose) {
      onClose();
    }
  };


  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      fullScreen={fullScreen}
      PaperProps={{
        sx: {
          borderRadius: {
            xs: 0,
            sm: '22px',
          },

          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        sx={{
          pb: 1,
        }}
      >
        <Typography
          variant="h4"
          sx={{
            fontWeight: 800,
          }}
        >
          Finish setting up your organization
        </Typography>

        <Typography
          sx={{
            mt: 1,
            opacity: 0.75,
            fontSize: '0.95rem',
            lineHeight: 1.6,
          }}
        >
          Confirm your business information before
          adding ATGs, users, and other organization
          resources. You can skip this for now and
          finish it later from the dashboard.
        </Typography>
      </DialogTitle>


      <Formik
        enableReinitialize

        initialValues={
          initialValues
        }

        validationSchema={
          organizationSchema
        }

        onSubmit={
          async (
            values,
            actions
          ) => {
            setSubmitError('');

            try {
              await completeSetup({
                name:
                  values.name.trim(),

                email:
                  values.email.trim(),

                phone:
                  values.phone.trim(),

                address:
                  values.address.trim(),

                type:
                  values.type,
              }).unwrap();


              /*
               * Refresh the JWT so it now
               * contains:
               *
               * organizationSetupComplete=true
               */
              await refresh().unwrap();


              if (onCompleted) {
                await onCompleted();
              }
            } catch (error) {
              console.error(
                'Organization setup failed:',
                error
              );

              setSubmitError(
                error?.data?.message ||
                  error?.message ||
                  'Unable to complete organization setup.'
              );
            } finally {
              actions.setSubmitting(
                false
              );
            }
          }
        }
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
          isSubmitting,
        }) => (
          <form
            onSubmit={
              handleSubmit
            }
          >
            <DialogContent
              dividers
              sx={{
                py: 3,
              }}
            >
              {submitError && (
                <Alert
                  severity="error"
                  sx={{
                    mb: 3,
                  }}
                >
                  {submitError}
                </Alert>
              )}


              <Box
                display="grid"
                gap="22px"
                gridTemplateColumns={{
                  xs:
                    '1fr',

                  sm:
                    'repeat(2, minmax(0, 1fr))',
                }}
              >
                <TextField
                  fullWidth
                  variant="filled"
                  type="text"
                  label="Organization Name"
                  name="name"

                  value={
                    values.name
                  }

                  onChange={
                    handleChange
                  }

                  onBlur={
                    handleBlur
                  }

                  error={
                    !!touched.name &&
                    !!errors.name
                  }

                  helperText={
                    touched.name &&
                    errors.name
                  }
                />


                <TextField
                  fullWidth
                  variant="filled"
                  type="email"
                  label="Organization Email"
                  name="email"

                  value={
                    values.email
                  }

                  onChange={
                    handleChange
                  }

                  onBlur={
                    handleBlur
                  }

                  error={
                    !!touched.email &&
                    !!errors.email
                  }

                  helperText={
                    touched.email &&
                    errors.email
                  }
                />


                <TextField
                  fullWidth
                  variant="filled"
                  type="tel"
                  label="Phone"
                  name="phone"

                  value={
                    values.phone
                  }

                  onChange={
                    handleChange
                  }

                  onBlur={
                    handleBlur
                  }

                  error={
                    !!touched.phone &&
                    !!errors.phone
                  }

                  helperText={
                    touched.phone &&
                    errors.phone
                  }
                />


                <FormControl
                  fullWidth
                  variant="filled"

                  error={
                    !!touched.type &&
                    !!errors.type
                  }
                >
                  <InputLabel
                    id="organization-type-label"
                  >
                    Organization Type
                  </InputLabel>

                  <Select
                    labelId="organization-type-label"
                    name="type"

                    value={
                      values.type
                    }

                    onChange={
                      handleChange
                    }

                    onBlur={
                      handleBlur
                    }
                  >
                    <MenuItem
                      value="Station Owner"
                    >
                      Station Owner
                    </MenuItem>

                    <MenuItem
                      value="Compliance"
                    >
                      Compliance
                    </MenuItem>
                  </Select>

                  {touched.type &&
                    errors.type && (
                      <FormHelperText>
                        {errors.type}
                      </FormHelperText>
                    )}
                </FormControl>


                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  variant="filled"
                  type="text"
                  label="Address"
                  name="address"

                  value={
                    values.address
                  }

                  onChange={
                    handleChange
                  }

                  onBlur={
                    handleBlur
                  }

                  error={
                    !!touched.address &&
                    !!errors.address
                  }

                  helperText={
                    touched.address &&
                    errors.address
                  }

                  sx={{
                    gridColumn: {
                      xs:
                        'auto',

                      sm:
                        '1 / -1',
                    },
                  }}
                />
              </Box>


              <Alert
                severity="info"
                sx={{
                  mt: 3,
                }}
              >
                This information can be updated later
                from your organization settings.
              </Alert>
            </DialogContent>


            <DialogActions
              sx={{
                px: 3,
                py: 2.5,
                gap: 1,
                flexDirection: {
                  xs:
                    'column-reverse',

                  sm:
                    'row',
                },
              }}
            >
              <Button
                type="button"
                onClick={
                  handleClose
                }

                disabled={
                  isSubmitting ||
                  isCompletingSetup ||
                  isRefreshing
                }

                sx={{
                  width: {
                    xs:
                      '100%',

                    sm:
                      'auto',
                  },
                }}
              >
                Skip for now
              </Button>


              <Button
                type="submit"
                variant="contained"
                color="secondary"

                disabled={
                  isSubmitting ||
                  isCompletingSetup ||
                  isRefreshing
                }

                sx={{
                  minWidth:
                    '220px',

                  width: {
                    xs:
                      '100%',

                    sm:
                      'auto',
                  },

                  fontWeight:
                    800,
                }}
              >
                {isCompletingSetup ||
                isRefreshing ? (
                  <CircularProgress
                    size={22}
                    color="inherit"
                  />
                ) : (
                  'Complete Setup'
                )}
              </Button>
            </DialogActions>
          </form>
        )}
      </Formik>
    </Dialog>
  );
};


export default OrganizationSetup;